import { useEffect, useRef } from "react";
import apiEndPoints from "./apiEndPoints";
import { apiCall, errorToast } from "./httpClient";
import store from "reduxStore/store";
import { userLogout } from "reduxStore/action/authActions";

/** JV Sign out (session_hash). */
export const SESSION_PULSE_POLL_MS = 10 * 1000;
/** JW inactive (login.status). */
export const SESSION_STATUS_POLL_MS = 5 * 60 * 1000;

export function isActiveUserSession(loginState) {
  const res = loginState?.response;
  return Boolean(
    loginState?.authToken &&
      res?.status === 200 &&
      res?.token &&
      (res?.data?._id || res?.data?.user_id),
  );
}

export function useSessionStatusPoll(isLoggedIn) {
  const inFlight = useRef(false);
  const loggingOut = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      loggingOut.current = false;
      return undefined;
    }

    const signOutQuietly = (message) => {
      if (loggingOut.current) {
        return;
      }
      loggingOut.current = true;
      errorToast(message || "Your account is inactive. Please contact the administrator.", {
        title: "Signed out",
        autoClose: 5000,
      });
      store.dispatch(userLogout());
    };

    const run = async (url) => {
      if (inFlight.current || loggingOut.current) {
        return;
      }
      inFlight.current = true;
      try {
        const res = await apiCall("post", url, { suppressGlobalLoader: true });
        const payload = res?.data;
        if (payload?.status === 401 || payload?.status === 403) {
          signOutQuietly(payload?.message);
        }
      } catch (error) {
        const httpStatus = error?.response?.status;
        const payload = error?.response?.data;
        if (
          httpStatus === 401 ||
          httpStatus === 403 ||
          payload?.status === 401 ||
          payload?.status === 403
        ) {
          signOutQuietly(payload?.message);
        }
      } finally {
        inFlight.current = false;
      }
    };

    const runPulse = () => run(apiEndPoints.SESSION_PULSE);
    const runStatus = () => run(apiEndPoints.SESSION_STATUS);

    runPulse();
    const pulseId = window.setInterval(runPulse, SESSION_PULSE_POLL_MS);
    const statusId = window.setInterval(runStatus, SESSION_STATUS_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        runPulse();
        runStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(pulseId);
      window.clearInterval(statusId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isLoggedIn]);
}
